<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import { Link, usePage } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'
import PrimaryButton from '@/Components/PrimaryButton.vue'
import InventoryIndexFilters from '@/Components/Inventory/InventoryIndexFilters.vue'
import PaginationLinks from '@/Components/PaginationLinks.vue'
import DialogModal from '@/Components/DialogModal.vue'
import SecondaryButton from '@/Components/SecondaryButton.vue'
import { computed, ref } from 'vue'
import axios from 'axios'
import ExportLoadingOverlay from '@/Components/ExportLoadingOverlay.vue'
import { useTenantTextRules } from '@/helpers'

const showCloseModal = ref(false)
const itemToClose = ref(null)
const exporting = ref(false)
const { resolveTextRule } = useTenantTextRules();

const openCloseModal = (item) => {
  itemToClose.value = item
  showCloseModal.value = true
}

const cancelClose = () => {
  showCloseModal.value = false
  itemToClose.value = null
}

const confirmClose = () => {
  if (!itemToClose.value) return

  Inertia.post(route('inventory.close-style', { styleId: itemToClose.value.id }), {}, {
    onSuccess: () => {
      showCloseModal.value = false
      itemToClose.value = null
    },
    onError: (e) => {
      console.error('Close failed', e)
    }
  })
}

const props = defineProps({
  filters: Object,
  clients: Array,
  garments: Array,
  scales: Array,
  fabrics: Array,
  boxes: Array,
  locations: Array,
  styleNumbers: Array,
  items: Object,
  userIsInternal: Boolean,
})

const inventoryTableStyleNumberLabel = computed(() =>
  resolveTextRule('inventory.table.style_number', 'Style Number', 'inventory.table.style_number')
)
const inventoryTableGarmentLabel = computed(() =>
  resolveTextRule('inventory.table.garment', 'Garment', 'inventory.table.garment')
)
const inventoryTableSizeScaleLabel = computed(() =>
  resolveTextRule('inventory.table.size_scale', 'Size Scale', 'inventory.table.size_scale')
)
const inventoryTitle = computed(() =>
  resolveTextRule('inventory.page.title', 'Inventory', 'inventory.page.title')
)
const inventoryNewStyleLabel = computed(() =>
  resolveTextRule('inventory.actions.new_style', 'New Style', 'inventory.actions.new_style')
)
const inventorySeeLogsLabel = computed(() =>
  resolveTextRule('inventory.actions.see_logs', 'See Logs', 'inventory.actions.see_logs')
)
const inventoryBulkLocationLabel = computed(() =>
  resolveTextRule(
    'inventory.actions.bulk_location_update',
    'Bulk Location Update',
    'inventory.actions.bulk_location_update'
  )
)
const inventoryReportViewEditLabel = computed(() =>
  resolveTextRule(
    'inventory.table.report_view_edit',
    'Report View/Edit',
    'inventory.table.report_view_edit'
  )
)
const inventoryStyleViewEditLabel = computed(() =>
  resolveTextRule(
    'inventory.table.style_view_edit',
    'Style View/Edit',
    'inventory.table.style_view_edit'
  )
)

const toggleActive = (item, nextValue) => {
  const routeName = nextValue ? 'inventory.open-style' : 'inventory.close-style'
  const payload = { styleId: item.id }

  Inertia.post(route(routeName, payload), {}, {
    preserveScroll: true,
    onError: (e) => {
      console.error('Toggle failed', e)
    }
  })
}

const downloadExport = () => {
  const queryParameters = { ...route().params };
  delete queryParameters.page;
  exporting.value = true

  queryParameters.filters = props.filters || {};

  axios({
    url: route('inventory.export', queryParameters),
    method: 'POST',
    responseType: 'blob'
  }).then(response => {
    const href = window.URL.createObjectURL(response.data);

    const anchorElement = document.createElement('a');
    anchorElement.href = href;
    anchorElement.download = response.headers['content-disposition']
      .split('filename=')[1]
      .split(';')[0]
      .replaceAll('"', '');

    document.body.appendChild(anchorElement);
    anchorElement.click();

    setTimeout(() => {
      document.body.removeChild(anchorElement);
      window.URL.revokeObjectURL(href);
      exporting.value = false
    }, 200);
  }).catch(error => {
    console.error('Export failed:', error);
    exporting.value = false
  });
}
</script>

<template>
  <AppLayout :title="inventoryTitle">
    <div class="mx-auto py-10 px-6">
      <div class="flex justify-between mb-4">
        <h2 class="font-semibold text-xl text-gray-800 leading-tight mb-4">{{ inventoryTitle }}</h2>
      </div>

      <ExportLoadingOverlay v-model="exporting" title="Exporting…"
        message="Generating file, please wait. File may take a few moments to download. Click to dismiss."
        :autoCloseOnFocus="true" :closeOnUserInteraction="true" :interactionArmDelayMs="1000" />

      <div class="mt-5 mb-2 md:grid lg:grid-cols-3 md:grid-cols-3 grid-cols-1 gap-2">
        <Link :href="route('inventory.style.create')" class="w-full">
        <PrimaryButton type="button" class="w-full">{{ inventoryNewStyleLabel }}</PrimaryButton>
        </Link>
        <Link :href="route('inventory.logs')" class="w-full">
        <PrimaryButton type="button" class="w-full">{{ inventorySeeLogsLabel }}</PrimaryButton>
        </Link>
        <Link :href="route('inventory.bulk-location')" class="w-full">
        <PrimaryButton type="button" class="w-full">{{ inventoryBulkLocationLabel }}</PrimaryButton>
        </Link>
      </div>

      <div class="mt-5 mb-5 gap-2">
        <InventoryIndexFilters :filters="filters" :clients="clients" :garments="garments" :scales="scales"
          :fabrics="fabrics" :boxes="boxes" :locations="locations" :style-numbers="styleNumbers" />
      </div>

      <div class="mt-5 mb-2 md:grid lg:grid-cols-3 md:grid-cols-3 grid-cols-1 gap-2">
        <PrimaryButton v-if="props.filters.client != null || props.filters.scaleNameId != null" type="button"
          class="w-full mb-2" @click="downloadExport">
          Generate Spreadsheet
        </PrimaryButton>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full w-full text-left text-sm font-light">
          <thead class="border-b font-medium dark:border-neutral-500">
            <tr>
              <th class="px-3 py-2 text-left">{{ inventoryTableStyleNumberLabel }}</th>
              <th class="px-3 py-2 text-left">{{ inventoryTableGarmentLabel }}</th>
              <th class="px-3 py-2 text-left">{{ inventoryTableSizeScaleLabel }}</th>
              <th class="px-3 py-2 text-left">Notes</th>
              <th class="px-3 py-2 text-center">{{ inventoryReportViewEditLabel }}</th>
              <th class="px-3 py-2 text-center">{{ inventoryStyleViewEditLabel }}</th>
              <th class="px-3 py-2 text-center">Active</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in props.items.data" class="border-b border-gray-200 hover:bg-gray-100">
              <td class="px-3 py-2">{{ item.styleNumber }}</td>
              <td class="px-3 py-2">{{ item.garment }}</td>
              <td class="px-3 py-2">{{ item.scale }}</td>
              <td class="px-3 py-2">{{ item.notes }}</td>

              <!-- Report View/Edit -->
              <td class="px-3 py-2 text-center">
                <Link :title="inventoryReportViewEditLabel" class="fa fa-pen p-1" :href="route('inventory.report.view', item)">
                </Link>
              </td>

              <!-- Style View/Edit -->
              <td class="px-3 py-2 text-center">
                <Link :title="inventoryStyleViewEditLabel" class="fa fa-pen p-1" :href="route('inventory.style.edit', item)">
                </Link>
              </td>

              <!-- Active Toggle -->
              <td class="px-3 py-2 text-center">
                <label class="inline-flex items-center cursor-pointer">
                  <input type="checkbox" class="sr-only peer" :checked="!!item.isActive"
                    @change="(e) => toggleActive(item, e.target.checked)" />
                  <div class="relative w-7 h-3.5 bg-gray-300 rounded-full transition-colors
             peer-focus:outline-none peer-focus:ring-1 peer-focus:ring-green-400
             peer-checked:bg-green-500
             after:content-[''] after:absolute after:w-2.5 after:h-2.5 after:bg-white after:rounded-full
             after:transition-all after:left-0.5 after:top-[2px]
             peer-checked:after:translate-x-3.5">
                  </div>
                </label>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="text-center mt-5">
          <PaginationLinks :pagination="items" />
        </div>
      </div>
    </div>
  </AppLayout>
  <DialogModal :show="showCloseModal" @close="cancelClose">
    <template #title>
      Close Style
    </template>

    <template #content>
      Are you sure you want to close the style
      <strong>{{ itemToClose?.styleNumber }}</strong>?
      This action cannot be undone.
    </template>

    <template #footer>
      <SecondaryButton @click="cancelClose">
        Cancel
      </SecondaryButton>
      <PrimaryButton class="mx-2" @click="confirmClose">
        Confirm Close
      </PrimaryButton>
    </template>
  </DialogModal>

</template>
